/* eslint-disable deprecation/deprecation */
import MuiButtonBase, {
  ButtonBaseProps as MuiButtonBaseProps,
  ButtonBaseTypeMap as MuiButtonBaseTypeMap,
} from '@mui/material/ButtonBase';
import { OverrideProps } from '@mui/material/OverridableComponent';
import * as React from 'react';
import {
  ExtendAndOmitTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';

/**
 * Props that we do not want to include with ButtonBase.
 *
 * Using `keyof Pick` provides type safety that ensures we are
 * only blocking props that are part of the interface.
 **/
type ButtonBaseBlockedProps = keyof Pick<
  MuiButtonBaseProps,
  | 'action'
  | 'centerRipple'
  | 'disableRipple'
  | 'disableTouchRipple'
  | 'focusRipple'
  | 'focusVisibleClassName'
  | 'TouchRippleProps'
  | 'touchRippleRef'
>;

export interface ButtonBaseOwnProps {
  href?: string;
}

export type ButtonBaseTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiButtonBaseTypeMap['defaultComponent']
> = ExtendAndOmitTypeMap<
  MuiButtonBaseTypeMap,
  AdditionalProps & ButtonBaseOwnProps,
  ButtonBaseBlockedProps,
  RootComponent
>;

/**
 * @deprecated Unsupported component: This is an internal component, use Button instead.
 * Please reach out to us in #unify-design-system-support if you have a use case for this component.
 */
export type ButtonBaseProps<
  RootComponent extends React.ElementType = MuiButtonBaseTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<
  ButtonBaseTypeMap<AdditionalProps, RootComponent>,
  RootComponent
>;

/**
 * `ButtonBase` contains as few styles as possible.
 * It aims to be a simple building block for creating a button.
 * It contains a load of style reset and some focus/ripple logic.
 *
 * Demos:
 *
 * - [Buttons](https://unify.workiva.org/components/button?tab=code)
 *
 * API:
 *
 * - [ButtonBase API](https://unify.workiva.org/components/button?tab=props)
 *
 * @deprecated Unsupported component: This is an internal component, use Button instead.
 * Please reach out to us in #unify-design-system-support if you have a use case for this component.
 */
export const ButtonBase = forwardRefToOverridableComponent<
  ButtonBaseTypeMap,
  ButtonBaseProps
>(function ButtonBase(props, ref) {
  return <MuiButtonBase {...props} ref={ref} />;
});

export default ButtonBase;
